import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LogOut, Menu, User, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import { ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/api";
import { LanguageSelect } from "@/components/LanguageSelect";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TYPE_ROUTES = {
  property: "/properties", tenant: "/tenants", room: "/rooms",
  contract: "/contracts", vendor: "/vendors", document: "/documents",
};

export function Topbar({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!q || q.length < 1) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results || []);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <header className="h-16 bg-white border-b border-border flex items-center gap-4 px-4 md:px-6 shrink-0 sticky top-0 z-30">
      <button className="lg:hidden p-2 -ml-2" onClick={onMenuClick} data-testid="menu-toggle">
        <Menu className="w-5 h-5" />
      </button>

      <div className="flex-1 max-w-xl relative" ref={boxRef}>
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("物件・入居者・契約・書類などを検索...")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          className="pl-9 bg-gray-50 border-transparent focus:bg-white focus:border-input"
          data-testid="global-search"
        />
        {open && (
          <div className="absolute top-12 left-0 right-0 bg-white border border-border rounded-sm shadow-md max-h-96 overflow-y-auto z-50">
            {results.length === 0 ? (
              <div className="px-4 py-6 text-sm text-muted-foreground text-center">{t("該当する結果がありません")}</div>
            ) : (
              results.map((r, i) => (
                <button
                  key={i}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between border-b border-border last:border-0"
                  onClick={() => { navigate(TYPE_ROUTES[r.type] || "/"); setOpen(false); setQ(""); }}
                  data-testid={`search-result-${i}`}
                >
                  <span className="text-sm">{r.title}</span>
                  <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-sm">{r.label}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <LanguageSelect variant="light" />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 pl-2 pr-1 py-1 hover:bg-gray-100 rounded-sm transition-colors" data-testid="user-menu">
            <div className="w-8 h-8 rounded-full bg-[#111111] text-white flex items-center justify-center text-sm font-medium">
              {(user?.name || "U").charAt(0)}
            </div>
            <div className="hidden md:block text-left leading-tight">
              <div className="text-sm font-medium text-[#0A0A0A]">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground">{t(ROLE_LABELS[user?.role]) || user?.role}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="font-medium">{user?.name}</div>
            <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />{t("ログアウト")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
